/**
 * Stone (Pagar.me v5) card tokenization in the browser.
 *
 * The card goes straight from the buyer's browser to Pagar.me with the public
 * key; zenpay only ever receives the short-lived token. Pagar.me only accepts
 * this call from domains registered in its dashboard.
 */
export interface StoneCardFields {
  number: string;
  holderName: string;
  /** "MM/AA" as typed. */
  expiry: string;
  cvv: string;
}

export const EMPTY_STONE_CARD: StoneCardFields = { number: "", holderName: "", expiry: "", cvv: "" };

const TOKENS_URL = "https://api.pagar.me/core/v5/tokens";

export function cardDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/** "4111111111111111" → "4111 1111 1111 1111" (Amex keeps 4-6-5). */
export function formatCardNumber(value: string): string {
  const digits = cardDigits(value).slice(0, 19);
  if (/^3[47]/.test(digits)) {
    return [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)].filter(Boolean).join(" ");
  }
  return digits.replace(/(.{4})/g, "$1 ").trim();
}

export function formatExpiry(value: string): string {
  const digits = cardDigits(value).slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
}

function luhnValid(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

export function parseExpiry(expiry: string, now = new Date()): { month: number; year: number } | null {
  const digits = cardDigits(expiry);
  if (digits.length !== 4) return null;
  const month = Number(digits.slice(0, 2));
  const year = 2000 + Number(digits.slice(2));
  if (month < 1 || month > 12) return null;
  const endOfMonth = new Date(year, month, 1);
  if (endOfMonth.getTime() <= now.getTime()) return null;
  return { month, year };
}

/** Which field is wrong, for the error message. Null when the card looks valid. */
export function invalidStoneCardField(
  card: StoneCardFields,
  now = new Date(),
): "number" | "holderName" | "expiry" | "cvv" | null {
  const digits = cardDigits(card.number);
  if (digits.length < 13 || digits.length > 19 || !luhnValid(digits)) return "number";
  if (normalizeHolderName(card.holderName).length < 2) return "holderName";
  if (!parseExpiry(card.expiry, now)) return "expiry";
  if (!/^\d{3,4}$/.test(cardDigits(card.cvv))) return "cvv";
  return null;
}

/**
 * Pagar.me refuses holder names with digits or special characters, and caps
 * them at 64. Accents and apostrophes are common in Brazilian names
 * ("JOSÉ D'ÁVILA"): strip them instead of failing the payment.
 */
export function normalizeHolderName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase()
    .slice(0, 64);
}

export class StoneTokenizeError extends Error {}

export async function tokenizeStoneCard(publicKey: string, card: StoneCardFields): Promise<string> {
  const expiry = parseExpiry(card.expiry);
  if (!expiry) throw new StoneTokenizeError("invalid_expiry");
  const res = await fetch(`${TOKENS_URL}?appId=${encodeURIComponent(publicKey)}`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      type: "card",
      card: {
        number: cardDigits(card.number),
        holder_name: normalizeHolderName(card.holderName),
        exp_month: expiry.month,
        exp_year: expiry.year,
        cvv: cardDigits(card.cvv),
      },
    }),
  });
  const json = (await res.json().catch(() => ({}))) as { id?: unknown; message?: unknown };
  if (!res.ok || typeof json.id !== "string") {
    throw new StoneTokenizeError(typeof json.message === "string" ? json.message : `tokenize_${res.status}`);
  }
  return json.id;
}
